import { computed, defineComponent, inject, onMounted, ref, watch } from '@vue/composition-api';
import SegmentationChart from 'components/charts/SegmentationChart/SegmentationChart.vue';
import DownloadExportButton from 'components/project/analysis/results/widgets/DownloadExportButton.vue';
import WidgetFrame from 'components/widgets/WidgetFrame/WidgetFrame.vue';
import WidgetMenu from 'components/DataWidgets/WidgetMenu/WidgetMenu.vue';
import DataToolTip from 'components/DataWidgets/DataToolTip/DataToolTip.vue';
import icon from 'assets/img/dashboards/dash-segmentation-new.svg';
import SelectorControls from 'components/widgets/SelectorControls/SelectorControls.vue';
import errorIcon from 'assets/icons/alert-bubble.svg';
import { makeRequirements } from './FieldSegmentationWidget.requirements';
import { menu } from './FieldSegmentationWidget.menu';
import { rowsForGroup, sortRows } from 'components/DataWidgets/FieldSegmentationWidget/FieldSegmentationWidget.utils';
import { makeBarChartSlide } from '../DataWidgetUtils';
const CompareSegmentationWidget = defineComponent({
    components: {
        SelectorControls,
        WidgetFrame,
        WidgetMenu,
        SegmentationChart,
        DataToolTip,
        DownloadExportButton,
    },
    props: {
        exportName: { type: String, required: false, default: '' },
        group: { type: String, required: false, default: 'overall__' },
        mode: { type: String, required: false, default: '' },
        sliceOneName: { type: String, required: true },
        sliceTwoName: { type: String, required: true },
        sliceOneQuery: { type: Object, required: false, default: null },
        sliceTwoQuery: { type: Object, required: false, default: null },
        devMode: { type: Boolean, required: false, default: false },
        isZoomed: { type: Boolean, required: false, default: false },
        /** maximum number of rows to show */
        maxRows: { type: Number, required: false, default: null },
        footerText: { type: String, required: false, default: '' },
        /** fields */
        segmentFields: { type: Array, required: false, default: () => [] },
        /** data to render */
        dataSlice1: { type: Object, required: false, default: null },
        dataSlice2: { type: Object, required: false, default: null },
        hasNps: { type: Boolean, required: false, default: false },
        /** route object for zoom button */
        zoomToRoute: { type: Object, required: false, default: null },
        /** widget banner to display */
        banner: { type: Object, default: () => null, required: false },
        /** Add a skeleton mask (used when reloading state between dashboards) */
        masked: { type: Boolean, required: false, default: false },
        config: { type: Object, required: false, default: null },
    },
    setup(props, { emit }) {
        const analytics = inject('analytics');
        const root = ref(null);
        const allowShowExpected = ref(false);
        const selectedField = ref(null);
        const selectedOrder = ref('Observed Frequency');
        const selection = ref(null);
        const hoveredRowForToolTip = ref(null);
        const legendToolTip = ref(0);
        const width = ref(300);
        const expectedTooltipText = computed(() => {
            let label = '';
            switch (props.mode) {
                case 'overview':
                    label = 'the filtered overall data';
                    break;
                case 'query':
                    label = 'theme';
                    break;
                case 'concept':
                    label = 'concept';
                    break;
            }
            return `Expected frequency is based on the conditional probability of the segment given ${label}`;
        });
        const isSubsetofOverall = computed(() => {
            return props.group && props.group !== 'overall__';
        });
        const menus = computed(() => {
            return menu(selectedField.value ?? '', props.segmentFields, selectedOrder.value, props.hasNps, false);
        });
        const hasErrored = computed(() => {
            return !!props.dataSlice1 && !!props.dataSlice1.error &&
                !!props.dataSlice2 && !!props.dataSlice2.error;
        });
        const isLoading = computed(() => {
            return props.dataSlice1 && props.dataSlice1.status === 'fetching' &&
                props.dataSlice2 && props.dataSlice2.status === 'fetching';
        });
        /** the rows for the currently selected field */
        const groupRows = computed(() => {
            if (!props.group || !selectedField.value)
                return [];
            const rows_slice1 = rowsForGroup(props.dataSlice1?.data?.payload, props.sliceOneQuery ? 'filter' : props.group, selectedField.value);
            const rows_slice2 = rowsForGroup(props.dataSlice2?.data?.payload, props.sliceTwoQuery ? 'filter_compare' : props.group, selectedField.value);
            // merge the two slices
            const rows = rows_slice1.map((row) => {
                const compareRow = rows_slice2.find((r) => r.segment === row.segment);
                return {
                    ...row,
                    frequency_compare: compareRow?.frequency ?? 0,
                    freqPercent_compare: compareRow?.freqPercent ?? 0,
                    diff: (compareRow?.freqPercent ?? 0) - row.freqPercent,
                    nps_compare: compareRow?.['NPS Category|nps__'] ?? 0,
                    nps_diff: (compareRow?.['NPS Category|nps__'] ?? 0) - row?.['NPS Category|nps__'],
                    nps: row?.['NPS Category|nps__'] ?? 0
                };
            });
            return sortRows(rows, selectedOrder.value);
        });
        const numberHiddenSegments = computed(() => {
            if (!groupRows.value || !props.maxRows || isLoading.value || hasErrored.value)
                return 0;
            return groupRows.value?.length - props.maxRows;
        });
        const rowsForChart = computed(() => {
            const rows = groupRows.value.slice(0, props.maxRows ?? Infinity);
            if (showingNPS.value) {
                return rows.map((data) => {
                    return {
                        label: data.segment,
                        indicator: null,
                        bars: [
                            { percent: data.nps, color: '#11ACDF' },
                            { percent: data.nps_compare, color: '#8064AA' },
                        ],
                        columns: [
                            { value: data.nps, label: data.nps.toFixed(2) },
                            { value: data.nps_compare, label: data.nps_compare.toFixed(2) },
                            { value: data.nps_diff, label: data.nps_diff.toFixed(2) },
                        ],
                    };
                });
            }
            else {
                return rows.map((data) => {
                    return {
                        label: data.segment,
                        indicator: null,
                        bars: [
                            { percent: data.freqPercent, color: '#11ACDF' },
                            { percent: data.freqPercent_compare, color: '#8064AA' },
                        ],
                        columns: [
                            { value: data.freqPercent, label: `${data.freqPercent.toFixed(2)}%` },
                            { value: data.freqPercent_compare, label: `${data.freqPercent_compare.toFixed(2)}%` },
                            { value: data.diff, label: `${data.diff.toFixed(2)}%` },
                        ],
                    };
                });
            }
        });
        const visibleRows = computed(() => {
            if (isLoading.value || !rowsForChart.value)
                return [];
            if (!selection.value || selection.value.length > rowsForChart.value.length) {
                return rowsForChart.value.slice(0, 20);
            }
            return selection.value.map((s) => rowsForChart.value[s]);
        });
        // The number of segments truncated by the selector controls
        const numberTruncatedSegments = computed(() => {
            return rowsForChart.value.length - visibleRows.value.length;
        });
        const legend = computed(() => {
            return [
                { label: props.sliceOneName, color: '#11ACDF' },
                { label: props.sliceTwoName, color: '#8064AA' },
            ];
        });
        const showingNPS = computed(() => {
            return selectedOrder.value === 'Highest NPS' || selectedOrder.value === 'Lowest NPS';
        });
        const hasFields = computed(() => {
            return !!(props.segmentFields && props.segmentFields.length);
        });
        /** Are the rows truncated based on the selector controls */
        const areTruncatedBySelector = computed(() => {
            return visibleRows.value.length < rowsForChart.value.length;
        });
        const showFooter = computed(() => {
            return hasFields.value && (numberHiddenSegments.value > 0 || props.footerText !== '' || areTruncatedBySelector.value);
        });
        const fieldNames = computed(() => {
            return hasFields.value ? groupRows.value.map(r => r.segment) : [];
        });
        const headings = computed(() => {
            let headings = [{ label: selectedField.value }];
            if (showingNPS.value) {
                headings.push({ label: 'NPS', color: '#11ACDF' });
                headings.push({ label: 'NPS', color: '#8064AA' });
                headings.push({ label: 'Diff.', color: '#95A6AC' });
            }
            else {
                headings.push({ label: 'Frequency', color: '#11ACDF' });
                headings.push({ label: 'Frequency', color: '#8064AA' });
                headings.push({ label: 'Diff.', color: '#95A6AC' });
            }
            return headings;
        });
        const toolTip = computed(() => {
            if (hoveredRowForToolTip.value === null ||
                !visibleRows.value ||
                !(hoveredRowForToolTip.value < visibleRows.value.length))
                return null;
            const row = rowForSelectionIndex(hoveredRowForToolTip.value);
            if (!row)
                return null;
            let toolTip = {
                title: row.segment !== '' ? row.segment : '(No Value)',
                action: 'Click for explore options',
                data: [
                    {
                        label: `${props.sliceOneName} frequency (#/%)`,
                        value: {
                            text: `${row.frequency} / ${row.freqPercent?.toFixed(2)}%`,
                            style: { 'font-weight': 'bold', color: '#11ACDF' },
                        }
                    },
                    {
                        label: `${props.sliceTwoName} frequency (#/%)`,
                        value: {
                            text: `${row.frequency_compare} / ${row.freqPercent_compare?.toFixed(2)}%`,
                            style: { 'font-weight': 'bold', color: '#8064AA' },
                        }
                    },
                ]
            };
            if (props.hasNps && row['NPS Category|nps__']) {
                toolTip.data = toolTip.data.concat({
                    label: `${props.sliceOneName} NPS`,
                    value: {
                        text: row.nps,
                        style: row['NPS Category|nps__'] === '' ? { 'color': '#95a6ac' } : { 'font-weight': 'bold' },
                    }
                }, {
                    label: `${props.sliceTwoName} NPS`,
                    value: {
                        text: row.nps_compare,
                        style: row['NPS Category|nps__'] === '' ? { 'color': '#8064AA' } : { 'font-weight': 'bold' },
                    }
                });
            }
            return toolTip;
        });
        const rowForSelectionIndex = (selectionIndex) => {
            if (selectionIndex === undefined ||
                selectionIndex === null ||
                !groupRows.value ||
                !groupRows.value?.length)
                return null;
            let indexOf = 0;
            if (selection.value === null) {
                indexOf = selectionIndex;
            }
            else {
                indexOf = selection.value[selectionIndex];
            }
            if (indexOf > groupRows.value.length)
                return {};
            return groupRows.value[indexOf];
        };
        const clickedSegment = (index) => {
            const row = rowForSelectionIndex(index);
            if (row) {
                emit('segment-clicked', selectedField.value, row.segment === '(No Value)' ? '' : row.segment);
            }
        };
        const fetchData = (force = false) => {
            const requirements_slice1 = makeRequirements(props.hasNps, selectedField.value, props.sliceOneQuery);
            const requirements_slice2 = makeRequirements(props.hasNps, selectedField.value, props.sliceTwoQuery);
            emit('requires', 'segmentation_slice1', { ...requirements_slice1 }, force, !props.sliceOneQuery);
            emit('requires', 'segmentation_slice2', { ...requirements_slice2 }, force, !props.sliceTwoQuery);
        };
        const refresh = () => window.location.reload();
        const reload = () => fetchData(true);
        const contact = () => {
            try {
                window.Intercom('show');
            }
            catch (e) {
                console.warn('intercom show failed');
            }
        };
        const getChartEl = () => {
            return root.value?.$el.querySelector('.svg-container');
        };
        const getSvgExportConfig = () => {
            const svgEl = getChartEl();
            if (!svgEl)
                return null;
            const bb = svgEl.getBoundingClientRect();
            return {
                dims: {
                    height: bb.height,
                    width: bb.width,
                },
                css: `
          svg {
            top: 0;
            left: 0;
          }
          .chart {
            background-color: #fff;
            cursor: default;
          }
          text.first {
            text-anchor: start;
          }
          text.end {
            text-anchor: end;
          }
          rect.bar-0 {
            fill: #11ACDF;
          }
          rect.bar-1 {
            fill: #8064AA;
          }
          rect.background {
            fill: #F1F1F1;
          }
          rect.indicator {
            fill: #8064AA;
          }
          text.sorted {
            fill: #068CCC;
            font-weight: bold;
          }
          .legend {
            cursor: default;
          }
        `
            };
        };
        const getCsvData = () => {
            if (selectedField.value === null)
                return [];
            return groupRows.value.map((row) => {
                let out = {
                    [selectedField.value ?? '']: row.segment,
                    [`${props.sliceOneName} Frequency (#)`]: row.frequency,
                    [`${props.sliceOneName} Frequency (%)`]: row.freqPercent,
                    [`${props.sliceTwoName} Frequency (#)`]: row.frequency_compare,
                    [`${props.sliceTwoName} Frequency (%)`]: row.freqPercent_compare,
                };
                if (props.hasNps && row['NPS Category|nps__']) {
                    out['NPS'] = row['NPS Category|nps__'];
                }
                return out;
            });
        };
        const updateConfig = () => {
            const options = {
                order: selectedOrder.value,
                field: selectedField.value,
                allowShowExpected: allowShowExpected.value,
            };
            const updated = Object.assign({}, props.config, { options });
            emit('config-changed', updated);
        };
        const setShowExpected = (showExpected, fromConfig) => {
            allowShowExpected.value = showExpected;
            fetchData();
            // don't track programmatic setting of this option
            if (!fromConfig) {
                analytics?.track.segmentationChart.changeShowExpected(allowShowExpected.value);
                updateConfig();
            }
        };
        const setMenuSelection = (menu, [_, selection], fromConfig) => {
            switch (menu) {
                case 'Field': {
                    // set field selection only if the selection exists on the menu, if it doesn't then
                    // set it to the first item on the menu
                    const fieldMenuOptions = menus.value[0].options?.[0]?.[0]?.options;
                    if (!fieldMenuOptions)
                        return;
                    if (selection && fieldMenuOptions?.some((option) => (typeof option === 'string' ? option : option.value) === selection)) {
                        selectedField.value = selection;
                    }
                    else {
                        selectedField.value = typeof fieldMenuOptions[0] === 'string' ? fieldMenuOptions[0] : fieldMenuOptions[0]?.value ?? null;
                    }
                    fetchData();
                    // don't track programmatic setting of this option
                    if (!fromConfig) {
                        analytics?.track.segmentationChart.changeField(selection, props.group);
                    }
                    break;
                }
                case 'Sort By':
                    // when the widget switches from showing expected we might need
                    // to reset the sort by selection as it might now be invalid
                    const sortByMenuOptions = menus.value[1].options?.[0]?.[0]?.options;
                    if (sortByMenuOptions?.includes(selection)) {
                        selectedOrder.value = selection;
                    }
                    else {
                        selectedOrder.value = 'Observed Frequency';
                    }
                    if (!fromConfig) { // don't track programmatic setting of this option
                        analytics?.track.segmentationChart.changeOrder(selection, props.group);
                    }
                    break;
            }
            if (!fromConfig) {
                updateConfig();
            }
        };
        const setOptionsFromConfig = () => {
            const sortBy = props.config?.options?.order ?? 'Observed Frequency';
            const field = props.config?.options?.field ?? props.segmentFields?.[0]?.name;
            const allowShowExpected = props.config?.options.allowShowExpected ?? true;
            setMenuSelection('Sort By', [null, sortBy], true);
            setMenuSelection('Field', [null, field], true);
            setShowExpected(allowShowExpected, true);
        };
        const updateRowToolTip = (index) => {
            hoveredRowForToolTip.value = index;
        };
        const makePptSlide = (pptx) => {
            if (!rowsForChart.value.length)
                return;
            const slide = pptx.addSlide();
            makeBarChartSlide(pptx, slide, [
                {
                    name: props.sliceOneName,
                    labels: rowsForChart.value.flatMap((r) => [r.label]),
                    values: rowsForChart.value.flatMap((r) => [r.columns[0].value]),
                },
                {
                    name: props.sliceTwoName,
                    labels: rowsForChart.value.flatMap((r) => [r.label]),
                    values: rowsForChart.value.flatMap((r) => [r.columns[1].value]),
                },
            ], `${props.exportName} - Compare Segmentation`, 'Frequency (%)', selectedField.value ?? '', {
                chartColors: ['11ACDF', '8064AA'],
                valAxisMaxVal: 100,
                showLegend: true,
            });
        };
        watch(() => props.config, () => {
            setOptionsFromConfig();
        }, {
            deep: true,
        });
        watch(() => props.sliceOneQuery, () => {
            setMenuSelection('Sort By', [null, selectedOrder.value], true);
            fetchData();
        }, {
            deep: true,
        });
        watch(() => props.sliceTwoQuery, () => {
            setMenuSelection('Sort By', [null, selectedOrder.value], true);
            fetchData();
        }, {
            deep: true,
        });
        watch(() => props.group, () => {
            fetchData();
        });
        onMounted(() => {
            setOptionsFromConfig();
            if (!selectedField.value) {
                setMenuSelection('Field', [null, props.segmentFields?.[0]?.name], true);
            }
            fetchData();
        });
        return {
            expectedTooltipText,
            isSubsetofOverall,
            menus,
            hasErrored,
            isLoading,
            numberHiddenSegments,
            rowsForChart,
            visibleRows,
            numberTruncatedSegments,
            legend,
            hasFields,
            areTruncatedBySelector,
            showFooter,
            fieldNames,
            headings,
            rowForSelectionIndex,
            toolTip,
            clickedSegment,
            icon,
            errorIcon,
            refresh,
            reload,
            contact,
            root,
            getSvgExportConfig,
            getChartEl,
            getCsvData,
            setShowExpected,
            setMenuSelection,
            updateRowToolTip,
            selectedField,
            allowShowExpected,
            width,
            legendToolTip,
            selectedOrder,
            makePptSlide,
        };
    }
});
export default CompareSegmentationWidget;
